import {FormattedMessage} from '@pinecast/i18n';
import * as React from 'react';

import Button, {ButtonGroup} from './Button';
import Dialog from './Dialog';
import FocusRecoveryProvider from './focusRecovery/FocusRecoveryProvider';
import {IsolatedModal} from './ModalLayer';
import {P} from './Text';
import type {LocalizedReactNode} from './types';

const ActResolver = Symbol('actresolver');
type Renderer = (resume: () => void, dismiss: () => void) => React.ReactNode;
type Act = {
  id: number;
  render: Renderer;
  [ActResolver]?: () => void;
};

const StageContext = React.createContext<{
  acts: Array<Act>;
  enqueue: (act: Act) => Promise<void>;
} | null>(null);

// The provider at the root of the dashboard. It runs every dialog, and it
// holds focus recovery, so that focus stays in place when the focused element
// goes away (see `FocusRecoveryProvider`).
export const StageProvider = ({children}: {children: React.ReactNode}) => {
  const [acts, setActs] = React.useState<Array<Act>>([]);
  const enqueue = React.useCallback((act: Act) => {
    return new Promise<void>(resolve => {
      act[ActResolver] = resolve;
      setActs(acts => [...acts, act]);
    });
  }, []);
  const onDismiss = React.useCallback((act: Act) => {
    setActs(acts => acts.filter(x => x !== act));
  }, []);
  const value = React.useMemo(() => ({acts, enqueue}), [acts, enqueue]);
  return (
    <FocusRecoveryProvider>
      <StageContext.Provider value={value}>
        {children}
        {acts.map(act => (
          <ActRender act={act} key={act.id} onDismiss={onDismiss} />
        ))}
      </StageContext.Provider>
    </FocusRecoveryProvider>
  );
};

const useStage = () => {
  const payload = React.useContext(StageContext);
  if (!payload) {
    throw new Error('Stage provider was not found in render tree');
  }
  return payload;
};

const ActRender = React.memo(
  ({act, onDismiss}: {act: Act; onDismiss: (act: Act) => void}) => {
    const dismiss = React.useCallback(() => onDismiss(act), [onDismiss, act]);
    const resumed = React.useRef(false);
    const resume = React.useCallback(() => {
      resumed.current = true;
      onDismiss(act);
    }, [onDismiss, act]);
    // The act's promise settles once its dialog has closed, and the page is
    // live again. Code that awaits it can then move focus, which the inert
    // page would refuse while the dialog is open.
    React.useEffect(
      () => () => {
        if (resumed.current) {
          act[ActResolver]!();
        }
      },
      [act],
    );
    return (
      <React.Fragment key={act.id}>
        {act.render(resume, dismiss)}
      </React.Fragment>
    );
  },
);

let i = 0;
export function getActId(): number {
  return i++;
}

function wrapBody(node: React.ReactNode) {
  if (!React.isValidElement(node) || node.type !== FormattedMessage) {
    return node;
  }
  return <P>{node}</P>;
}

export const useAlertBox = () => {
  const {enqueue} = useStage();
  return React.useCallback(
    (
      title: LocalizedReactNode,
      body: React.ReactNode,
      continueButton: React.ReactNode = (
        <FormattedMessage
          description="Button to dismiss an alert in an affirmative way"
          defaultMessage="Okay"
        />
      ),
    ) => {
      return enqueue({
        id: getActId(),
        render(onContinue) {
          return (
            <IsolatedModal onBackdropClick={onContinue} onEscape={onContinue}>
              <Dialog
                actions={<Button onClick={onContinue}>{continueButton}</Button>}
                size="small"
                title={title}
              >
                {wrapBody(body)}
              </Dialog>
            </IsolatedModal>
          );
        },
      });
    },
    [enqueue],
  );
};

export const useConfirmBox = () => {
  const {enqueue} = useStage();
  return React.useCallback(
    (
      title: LocalizedReactNode,
      body: React.ReactNode,
      continueButton: React.ReactNode = (
        <FormattedMessage
          description="Button to dismiss an alert in an affirmative way"
          defaultMessage="Okay"
        />
      ),
      cancelButton: React.ReactNode = (
        <FormattedMessage
          description="Button to dismiss an alert in a negative way"
          defaultMessage="Cancel"
        />
      ),
    ) => {
      return enqueue({
        id: getActId(),
        render(onContinue, onDismiss) {
          return (
            // Escape does what Cancel does.
            <IsolatedModal onEscape={onDismiss}>
              <Dialog
                actions={
                  <ButtonGroup>
                    <Button onClick={onDismiss}>{cancelButton}</Button>
                    <Button $isPrimary onClick={onContinue}>
                      {continueButton}
                    </Button>
                  </ButtonGroup>
                }
                size="small"
                title={title}
              >
                {wrapBody(body)}
              </Dialog>
            </IsolatedModal>
          );
        },
      });
    },
    [enqueue],
  );
};

// Escape dismisses the dialog, as a Cancel button would. Pass
// `closable: false` for a dialog that must not close, and give it a visible
// control that finishes it. While the dialog cannot be cancelled, such as
// while it saves, its content calls `useHoldModalOpen`.
//
// A click on the backdrop does not close it. Most of these dialogs hold a
// form, and a click that misses the dialog would throw away what was typed.
export const useCustomModal = ({
  closable = true,
}: {closable?: boolean} = {}) => {
  const {enqueue} = useStage();
  return React.useCallback(
    (render: Renderer) =>
      enqueue({
        id: getActId(),
        render: (resume, dismiss) => (
          <IsolatedModal onEscape={closable ? dismiss : undefined}>
            {render(resume, dismiss)}
          </IsolatedModal>
        ),
      }),
    [closable, enqueue],
  );
};
